<template>
  <div class="pdf-preview-container">
    <!-- Loading State -->
    <div v-if="isPdfGenerating" class="pdf-loading">
      <div class="modern-spinner">
        <div class="spinner-ring"></div>
      </div>
      <p class="loading-text">Generating PDF preview...</p>
    </div>
    
    <!-- Error State -->
    <div v-else-if="pdfError" class="pdf-error">
      <div class="error-icon">
        <svg class="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      </div>
      <p class="error-message">{{ pdfError }}</p>
      <button @click="regeneratePdf" class="retry-button">
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
        </svg>
        Try Again
      </button>
    </div>
    
    <!-- PDF Display Container -->
    <div v-else-if="pdfPreviewData" class="pdf-display-container">
      <div ref="pdfContainer" class="pdf-embed-container"></div>
    </div>
    
    <!-- Initial State -->
    <div v-else class="pdf-placeholder">
      <div class="placeholder-content">
        <div class="placeholder-icon">
          <svg class="w-20 h-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
        </div>
        <h3 class="placeholder-title">PDF Preview</h3>
        <p class="placeholder-text">PDF preview will appear here</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { usePdfPreviewGeneration } from '../composables/usePdfPreviewGeneration.js'

export default {
  name: 'PdfOnlyPreview',
  props: {
    cvData: {
      type: Object,
      required: true,
      default: () => ({
        personalInfo: {},
        educationList: [],
        experienceList: [],
        activitiesList: [],
        skillCategories: []
      })
    },
    suspended: {
      type: Boolean,
      default: false
    }
  },
  emits: ['page-count-changed', 'generating-change'],
  setup(props, { emit }) {
    const { isPdfGenerating, pdfError, generatePdfPreview, embedPdfIntoContainer, cleanupPdfUrl } = usePdfPreviewGeneration()
    const pdfPreviewData = ref(null)
    const pdfContainer = ref(null)

    let debounceTimer = null

    // Generate PDF preview
    const generatePdf = async () => {
      // Clean up previous PDF
      if (pdfPreviewData.value?.url) {
        cleanupPdfUrl(pdfPreviewData.value.url)
      }
      pdfPreviewData.value = null
      emit('generating-change', true)

      const previewData = await generatePdfPreview(props.cvData)

      emit('generating-change', false)

      if (previewData && previewData.url) {
        pdfPreviewData.value = previewData

        // Emit page count
        emit('page-count-changed', previewData.pageCount || 1)
        
        // Wait for DOM update then embed PDF
        await nextTick()
        if (pdfContainer.value) {
          embedPdfIntoContainer(pdfContainer.value, previewData.url)
        }
      }
    }
    
    // Debounced PDF generation — skipped while suspended (e.g. during download)
    const debouncedGeneratePdf = () => {
      if (props.suspended) return
      clearTimeout(debounceTimer)
      debounceTimer = setTimeout(generatePdf, 500)
    }
    
    // Regenerate PDF on error
    const regeneratePdf = () => {
      generatePdf()
    }
    
    // Watch for data changes and regenerate PDF
    watch(() => props.cvData, () => {
      debouncedGeneratePdf()
    }, { deep: true })
    
    // Generate initial PDF on mount
    onMounted(async () => {
      await nextTick()
      generatePdf()
    })
    
    // Cleanup on unmount
    onUnmounted(() => {
      clearTimeout(debounceTimer)
      if (pdfPreviewData.value?.url) {
        cleanupPdfUrl(pdfPreviewData.value.url)
      }
    })
    
    return {
      isPdfGenerating,
      pdfError,
      pdfPreviewData,
      pdfContainer,
      regeneratePdf
    }
  }
}
</script>

<style scoped>
.pdf-preview-container {
  width: 100%;
  height: 100%;
  background: #f8f9fa;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  min-height: 700px;
}

/* Loading State */
.pdf-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #667eea;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.modern-spinner {
  position: relative;
  width: 64px;
  height: 64px;
  margin-bottom: 24px;
}

.spinner-ring {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid rgba(102, 126, 234, 0.2);
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: modernSpin 1s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes modernSpin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  font-weight: 500;
  opacity: 0.8;
  letter-spacing: 0.5px;
}

/* Error State */
.pdf-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: white;
  text-align: center;
  padding: 40px;
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%);
}

.error-icon {
  margin-bottom: 20px;
  opacity: 0.9;
}

.error-message {
  font-size: 16px;
  margin-bottom: 24px;
  opacity: 0.9;
  max-width: 400px;
}

.retry-button {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
}

.retry-button:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-2px);
}

/* Placeholder State */
.pdf-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.placeholder-content {
  text-align: center;
  color: #6c757d;
}

.placeholder-icon {
  margin-bottom: 16px;
  opacity: 0.6;
}

.placeholder-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  opacity: 0.8;
}

.placeholder-text {
  font-size: 16px;
  font-weight: 500;
  opacity: 0.8;
}

/* PDF Display */
.pdf-display-container {
  width: 100%;
  height: 100%;
  background: white !important;
  border-radius: 16px;
  overflow: hidden;
  border: none !important;
  box-shadow: none !important;
  padding: 0; /* Remove padding since we're using overflow trick */
}

.pdf-embed-container {
  width: 100%;
  height: 100%;
  border: none !important; /* Remove the border since PDF will overhang */
  border-radius: 14px;
  background: white !important;
  outline: none !important;
  box-shadow: none !important;
  position: relative; /* Enable absolute positioning for PDF element */
  overflow: hidden; /* Hide the overhanging PDF edges */
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pdf-preview-container {
    border-radius: 12px;
    min-height: 500px;
  }
  
  .pdf-display-container {
    border-radius: 12px;
  }
  
  .pdf-embed-container {
    border-radius: 12px;
  }
}
</style>